// =============================================================================
// components/nav/DrawerMenu.tsx — X-style slide-out account drawer (P2-DRAWER).
//
// An account drawer that slides in from the LEFT over a dimming scrim, opened by
// tapping the top-left avatar on the main screens and closed by a scrim tap, a
// left-swipe, or Android back. Modelled on X's (Twitter) account drawer STRUCTURE
// — a profile header (avatar, name, @handle, following/followers) over grouped
// rows: quick CREATE actions (New post, Add story), the main pages (Profile,
// Markets, Learn, Communities, Bookmarks, Notifications, Wallet), then Settings
// and Log out — rendered in CRYB's LIGHT lavender-glass style: a semi-transparent
// GlassSheet panel over a light lavender backing, not a dark sidebar.
//
// MOUNTING (App.tsx level): this is mounted ABOVE the tab navigator, as a sibling
// of InAppNotification/IncomingCallHandler, so it overlays every screen and can
// route from anywhere via navigationRef. It renders `null` (a zero-cost overlay)
// until opened, so it never intercepts touches while closed.
//
// CONTROLLER: any screen opens the drawer by calling the exported `openDrawer()`
// (a module-level event bus, mirroring the notifyAuthGate() pattern) — the top-
// left avatar's onPress just calls it. No prop-drilling, no context, no coupling
// to the navigator. `closeDrawer()` is exported for symmetry.
//
// MOTION: a single reanimated shared value (0 closed → 1 open) drives BOTH the
// panel translateX and the scrim opacity, settled with animationTokens.spring so
// the whole app shares one motion language. The close-swipe pan writes the same
// value directly so the panel tracks the finger.
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import {
  BackHandler,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';

import { GlassSheet } from '../glass';
import Avatar from '../ui/Avatar';
import LinearGradient from 'react-native-linear-gradient';
import { Icon, IoniconName } from '../ui/Icon';
import {
  animationTokens,
  colorTokens,
  colors,
  radius,
  spacing,
  typographyScale,
  fonts,
} from '../../constants/theme';
import { navigate } from '../../navigation/navigationRef';
import { getMe, logout } from '../../services/api';
import { friendlyDisplayName } from '../../lib/identity';
import { CRYPTO_LIVE, comingSoon } from '../../lib/comingSoon';

// --- Global open/close controller (mirrors notifyAuthGate) -------------------
// The avatar tap on a main screen has no reference to this overlay, so we expose
// a tiny module-level bus. openDrawer()/closeDrawer() from anywhere; the mounted
// <DrawerMenu /> subscribes and animates.
type DrawerListener = (open: boolean) => void;
const drawerListeners = new Set<DrawerListener>();

/** Open the account drawer from anywhere (e.g. the top-left avatar's onPress). */
export function openDrawer(): void {
  drawerListeners.forEach((l) => { try { l(true); } catch {} });
}

/** Close the account drawer from anywhere. */
export function closeDrawer(): void {
  drawerListeners.forEach((l) => { try { l(false); } catch {} });
}

// Left-swipe distance (pt) that commits the close, or a flick this fast (pt/s).
const CLOSE_DISTANCE = 80;
const FLICK_VELOCITY = 650;

// Reuse the app's shared spring so the drawer settles like every other surface.
const SPRING = {
  damping: animationTokens.spring.damping,
  stiffness: animationTokens.spring.stiffness,
  mass: animationTokens.spring.mass,
  overshootClamping: true,
} as const;

interface Me {
  id?: string;
  username?: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  followers: number;
  following: number;
  isLeader?: boolean;
}

type Row = {
  key: string;
  label: string;
  icon: IoniconName;
  onPress: () => void;
  danger?: boolean;
  // 'accent' tints the icon+label in brand primary (used for the CREATE actions);
  // 'soon' appends a "Soon" pill for gated (coming-soon) destinations.
  accent?: boolean;
  soon?: boolean;
};

export default function DrawerMenu() {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // Light drawer: CRYB's brand is the lavender GLASS world — the panel + text read
  // against a semi-transparent light glass surface over a lavender backing.
  const t = colorTokens.light;

  // Panel occupies most of the width (X-style), capped so it never spans a tablet.
  const panelWidth = Math.min(width * 0.82, 360);

  // 0 = fully off-screen left (closed) … 1 = fully on-screen (open). The pan and
  // the open/close springs all write this; the panel + scrim derive from it.
  const progress = useSharedValue(0);
  // JS mirror: gates mount + pointerEvents + Android back handling.
  const [mounted, setMounted] = useState(false);
  const [me, setMe] = useState<Me | null>(null);

  const open = useCallback(() => {
    setMounted(true);
    progress.value = withSpring(1, SPRING);
  }, [progress]);

  // Animate out, then unmount once settled (runOnJS off the spring completion).
  const finishClose = useCallback(() => setMounted(false), []);
  const close = useCallback(() => {
    progress.value = withSpring(0, SPRING, (finished) => {
      if (finished) runOnJS(finishClose)();
    });
  }, [progress, finishClose]);

  // Subscribe to the global controller.
  useEffect(() => {
    const listener: DrawerListener = (next) => (next ? open() : close());
    drawerListeners.add(listener);
    return () => { drawerListeners.delete(listener); };
  }, [open, close]);

  // Load the current user's identity + counts on first open (fresh each open so
  // follower/following stay current). Fail-soft: a network miss just shows the
  // friendly fallback identity with zeroed counts.
  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getMe();
        const d: any = res?.data ?? res;
        const u: any = d?.user ?? d;
        if (!u || cancelled) return;
        setMe({
          id: u.id,
          username: u.username,
          displayName: u.displayName,
          avatarUrl: u.avatarUrl ?? u.avatar ?? null,
          followers: u.followersCount ?? u.followers ?? u._count?.followers ?? 0,
          following: u.followingCount ?? u.following ?? u._count?.following ?? 0,
          isLeader: !!u.isLeader,
        });
      } catch {
        // fail-soft — keep whatever we had (or nothing).
      }
    })();
    return () => { cancelled = true; };
  }, [mounted]);

  // Android hardware back closes the drawer instead of leaving the screen.
  useEffect(() => {
    if (!mounted) return undefined;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      close();
      return true;
    });
    return () => sub.remove();
  }, [mounted, close]);

  // Navigate to a route, then close (close after so the drawer slides out over
  // the new screen — matches X's dismiss).
  const go = useCallback((fn: () => void) => {
    fn();
    close();
  }, [close]);

  // Open the current user's profile — but ONLY once identity has loaded, since
  // UserProfile requires a non-empty `username` param (passing undefined would
  // crash the profile screen / navigator). If it's not ready yet, no-op + close.
  const goToOwnProfile = useCallback(() => {
    const username = me?.username;
    if (!username) { close(); return; }
    go(() => navigate('UserProfile', { username }));
  }, [me?.username, go, close]);

  const handleLogout = useCallback(() => {
    close();
    // logout() emits the auth change → the root gate swaps to the Auth stack.
    logout().catch(() => {});
  }, [close]);

  // Left-swipe to close; the pan drives `progress` directly so the panel tracks
  // the finger, then commits on distance or a flick.
  const closePan = Gesture.Pan()
    .activeOffsetX(-12)
    .failOffsetX(24)
    .onUpdate((e) => {
      // translationX is negative when dragging left; map to a 1→0 progress.
      const p = 1 + e.translationX / panelWidth;
      progress.value = Math.min(1, Math.max(0, p));
    })
    .onEnd((e) => {
      const shouldClose = -e.translationX >= CLOSE_DISTANCE || e.velocityX <= -FLICK_VELOCITY;
      if (shouldClose) {
        progress.value = withSpring(0, SPRING, (finished) => {
          if (finished) runOnJS(finishClose)();
        });
      } else {
        progress.value = withSpring(1, SPRING);
      }
    });

  const scrimStyle = useAnimatedStyle(() => ({
    opacity: progress.value * 0.5,
  }));
  const panelStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: (progress.value - 1) * panelWidth }],
  }));

  if (!mounted) return null;

  const displayName = friendlyDisplayName(me?.displayName, me?.username);
  const handle = me?.username ? `@${me.username}` : '';

  // Quick CREATE actions — mirror X's floating compose, surfaced at the top of the
  // drawer so posting/story is one tap from anywhere. Both are existing modals.
  const createRows: Row[] = [
    {
      key: 'new-post',
      label: 'New post',
      icon: 'create-outline',
      onPress: () => go(() => navigate('CreatePost')),
      accent: true,
    },
    {
      key: 'add-story',
      label: 'Add story',
      icon: 'add-circle-outline',
      onPress: () => go(() => navigate('StoryCreate')),
      accent: true,
    },
  ];

  // Main pages — every route below is registered in the root stack. Wallet and
  // Markets are crypto/web3 surfaces gated to v2: they always show the "Soon"
  // pill and fire the shared comingSoon() gate instead of routing, unless/until
  // CRYPTO_LIVE flips true in v2 (per lib/comingSoon). Everything else routes.
  const pageRows: Row[] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: 'person-outline',
      onPress: goToOwnProfile,
    },
    // CRYB Leader flow: approved leaders get the Leader Dashboard; everyone else
    // sees the entry to apply. Gated on the isLeader flag from /users/me.
    me?.isLeader
      ? {
          key: 'leader-dashboard',
          label: 'Leader Dashboard',
          icon: 'ribbon-outline',
          onPress: () => go(() => navigate('LeaderDashboard')),
        }
      : {
          key: 'become-leader',
          label: 'Become a CRYB Leader',
          icon: 'ribbon-outline',
          onPress: () => go(() => navigate('LeaderApply')),
        },
    {
      key: 'markets',
      label: 'Markets',
      icon: 'trending-up-outline',
      soon: !CRYPTO_LIVE,
      onPress: CRYPTO_LIVE
        ? () => go(() => navigate('MarketsHub'))
        : () => { comingSoon('Markets'); close(); },
    },
    {
      key: 'learn',
      label: 'Learn',
      icon: 'school-outline',
      onPress: () => go(() => navigate('LearnIndex')),
    },
    {
      key: 'communities',
      label: 'Communities',
      icon: 'people-outline',
      onPress: () => go(() => navigate('Communities')),
    },
    {
      key: 'bookmarks',
      label: 'Bookmarks',
      icon: 'bookmark-outline',
      onPress: () => go(() => navigate('Bookmarks')),
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: 'notifications-outline',
      onPress: () => go(() => navigate('Notifications')),
    },
    {
      key: 'wallet',
      label: 'Wallet',
      icon: 'wallet-outline',
      soon: !CRYPTO_LIVE,
      onPress: CRYPTO_LIVE
        ? () => go(() => navigate('Wallet'))
        : () => { comingSoon('Wallet'); close(); },
    },
  ];

  // Footer rows — settings + destructive logout.
  const footerRows: Row[] = [
    {
      key: 'settings',
      label: 'Settings',
      icon: 'settings-outline',
      onPress: () => go(() => navigate('Settings')),
    },
    {
      key: 'logout',
      label: 'Log out',
      icon: 'log-out-outline',
      onPress: handleLogout,
      danger: true,
    },
  ];

  const renderRow = (row: Row) => (
    <Pressable
      key={row.key}
      style={({ pressed }) => [
        styles.row,
        pressed && {
          backgroundColor: row.danger
            ? 'rgba(255,71,87,0.10)'
            : row.accent
              ? 'rgba(108,92,231,0.12)'
              : 'rgba(108,92,231,0.08)',
        },
      ]}
      onPress={row.onPress}
      accessibilityRole="button"
      accessibilityLabel={row.label}
    >
      <Icon
        name={row.icon}
        size={22}
        color={row.danger ? t.error : row.accent ? t.primary : t.text}
      />
      <Text
        style={[
          styles.rowLabel,
          { color: row.danger ? t.error : row.accent ? t.primary : t.text },
        ]}
      >
        {row.label}
      </Text>
      {row.soon && (
        <View style={[styles.soonPill, { backgroundColor: 'rgba(108,92,231,0.12)' }]}>
          <Text style={[styles.soonText, { color: t.primary }]}>Soon</Text>
        </View>
      )}
    </Pressable>
  );

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      {/* Dimming scrim — tap to close. */}
      <Animated.View style={[styles.scrim, scrimStyle]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={close}
          accessibilityRole="button"
          accessibilityLabel="Close menu"
        />
      </Animated.View>

      {/* Glass panel — slides from the left, swipe-left to close. Starts at y=0
          (covers the status bar); the header block owns the safe-area top inset
          so no strip of the screen behind ever shows through the top. */}
      <GestureDetector gesture={closePan}>
        <Animated.View
          style={[styles.panelWrap, { width: panelWidth }, panelStyle]}
        >
          {/* Solid LIGHT lavender backing so the semi-transparent glass panel is
              opaque to the very top edge (covers the status bar) and reads over
              CRYB's light brand backdrop, never the screen behind it. */}
          <View style={styles.panelBacking} pointerEvents="none" />
          <GlassSheet
            grabber={false}
            mode="light"
            radius={radius.xl}
            padding={0}
            style={styles.panel}
            contentStyle={styles.panelContent}
          >
            {/* Scrollable so the full menu never clips/squishes on shorter devices
                (the cards + rows overflowed with no scroll → text/asset clipping). */}
            <ScrollView
              style={styles.drawerScroll}
              contentContainerStyle={{ paddingBottom: spacing.xl + insets.bottom }}
              showsVerticalScrollIndicator={false}
            >
            {/* Profile header — padded by the safe-area top inset so the avatar /
                name sit below the status bar, while the glass itself reaches y=0. */}
            <Pressable
              style={({ pressed }) => [
                styles.header,
                { paddingTop: insets.top + spacing.lg },
                pressed && styles.headerPressed,
              ]}
              onPress={goToOwnProfile}
              accessibilityRole="button"
              accessibilityLabel="Open your profile"
            >
              <Avatar uri={me?.avatarUrl} name={displayName} size={56} />
              <Text style={[styles.name, { color: t.text }]} numberOfLines={1}>
                {displayName}
              </Text>
              {!!handle && (
                <Text style={[styles.handle, { color: t.textMuted }]} numberOfLines={1}>
                  {handle}
                </Text>
              )}
              <View style={styles.stats}>
                <Pressable
                  style={({ pressed }) => [styles.stat, pressed && styles.statPressed]}
                  onPress={() => { if (me?.username) go(() => navigate('FollowList', { username: me.username, mode: 'following' })); }}
                  accessibilityRole="button"
                  accessibilityLabel="View following"
                >
                  <Text style={[styles.statNum, { color: t.text }]}>
                    {(me?.following ?? 0).toLocaleString()}
                  </Text>
                  <Text style={[styles.statLabel, { color: t.textMuted }]}> Following</Text>
                </Pressable>
                <Pressable
                  style={({ pressed }) => [styles.stat, pressed && styles.statPressed]}
                  onPress={() => { if (me?.username) go(() => navigate('FollowList', { username: me.username, mode: 'followers' })); }}
                  accessibilityRole="button"
                  accessibilityLabel="View followers"
                >
                  <Text style={[styles.statNum, { color: t.text }]}>
                    {(me?.followers ?? 0).toLocaleString()}
                  </Text>
                  <Text style={[styles.statLabel, { color: t.textMuted }]}> Followers</Text>
                </Pressable>
              </View>
            </Pressable>

            <View style={[styles.divider, { backgroundColor: t.border }]} />

            {/* Quick-access web3 cards (client ask): Trading/Analytics, then Wallet
                below. Crypto surfaces are v2-gated so these are branded teasers —
                no fabricated balances — that fire the shared comingSoon() gate. */}
            <View style={styles.quickCards}>
              <Pressable
                style={styles.quickCardPress}
                onPress={CRYPTO_LIVE ? () => go(() => navigate('MarketsHub')) : () => { comingSoon('Markets'); close(); }}
                accessibilityRole="button"
                accessibilityLabel="Trading and analytics"
              >
                <LinearGradient
                  colors={['#6C5CE7', '#5B8DEF']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.quickCard}
                >
                  <View style={styles.quickIconChip}>
                    <Icon name="trending-up" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.quickCardBody}>
                    <Text style={styles.quickCardTitle} numberOfLines={1}>Trading & Analytics</Text>
                    <Text style={styles.quickCardSub} numberOfLines={1}>Markets and your performance</Text>
                  </View>
                  {!CRYPTO_LIVE && (
                    <View style={styles.quickSoon}><Text style={styles.quickSoonText}>Soon</Text></View>
                  )}
                </LinearGradient>
              </Pressable>
              <Pressable
                style={styles.quickCardPress}
                onPress={CRYPTO_LIVE ? () => go(() => navigate('Wallet')) : () => { comingSoon('Wallet'); close(); }}
                accessibilityRole="button"
                accessibilityLabel="Wallet"
              >
                <LinearGradient
                  colors={['#A855F7', '#EC4899']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.quickCard}
                >
                  <View style={styles.quickIconChip}>
                    <Icon name="wallet" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.quickCardBody}>
                    <Text style={styles.quickCardTitle} numberOfLines={1}>Wallet</Text>
                    <Text style={styles.quickCardSub} numberOfLines={1}>Your on-chain balance</Text>
                  </View>
                  {!CRYPTO_LIVE && (
                    <View style={styles.quickSoon}><Text style={styles.quickSoonText}>Soon</Text></View>
                  )}
                </LinearGradient>
              </Pressable>
            </View>

            <View style={[styles.divider, { backgroundColor: t.border }]} />

            {/* Quick create actions */}
            <View style={styles.rows}>{createRows.map(renderRow)}</View>

            <View style={[styles.divider, { backgroundColor: t.border }]} />

            {/* Main pages */}
            <View style={styles.rows}>{pageRows.map(renderRow)}</View>

            <View style={[styles.divider, { backgroundColor: t.border }]} />

            {/* Settings + log out */}
            <View style={styles.rows}>{footerRows.map(renderRow)}</View>
            </ScrollView>
          </GlassSheet>
        </Animated.View>
      </GestureDetector>
    </View>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000000',
  },
  panelWrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
  },
  // Opaque LIGHT lavender fill behind the translucent glass so the panel is solid
  // to y=0 and the glass reads over the brand light backdrop (never dark).
  panelBacking: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.bg,
    borderTopRightRadius: radius.xl,
    borderBottomRightRadius: radius.xl,
  },
  panel: {
    flex: 1,
    // Square the left edge (flush to screen) so only the trailing edge is rounded.
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  panelContent: {
    flex: 1,
    paddingHorizontal: spacing.xl,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  drawerScroll: { flex: 1 },
  header: {
    paddingBottom: spacing.lg,
  },
  headerPressed: {
    opacity: 0.7,
  },
  name: {
    ...typographyScale.h3,
    ...fonts.bold,
    marginTop: spacing.md,
  },
  handle: {
    ...typographyScale.body,
    marginTop: spacing.xs / 2,
  },
  stats: {
    flexDirection: 'row',
    marginTop: spacing.md,
  },
  stat: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginRight: spacing.lg,
  },
  statPressed: {
    opacity: 0.6,
  },
  statNum: {
    ...typographyScale.body,
    ...fonts.bold,
  },
  statLabel: {
    ...typographyScale.caption,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginBottom: spacing.sm,
  },
  rows: {
    marginTop: spacing.xs,
  },
  quickCards: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  quickCardPress: { width: '100%' },
  quickCard: {
    width: '100%',           // bound to the row so the body shrinks + text truncates
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm + 2,
    padding: spacing.md,
    minHeight: 72,
    borderRadius: radius.lg,
    overflow: 'hidden', // keep the gradient + Soon pill inside the rounded card
  },
  quickIconChip: {
    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignItems: 'center', justifyContent: 'center',
  },
  quickIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickCardBody: { flex: 1, minWidth: 0, paddingRight: 44 },
  quickCardTitle: { ...fonts.bold, fontSize: 14, color: '#FFFFFF' },
  quickCardSub: { ...fonts.medium, fontSize: 11, color: 'rgba(255,255,255,0.82)', marginTop: 1 },
  quickSoon: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    backgroundColor: 'rgba(255,255,255,0.24)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
  },
  quickSoonText: { ...fonts.bold, fontSize: 10, color: '#FFFFFF', letterSpacing: 0.3 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    marginHorizontal: -spacing.md,
    borderRadius: radius.md,
  },
  rowLabel: {
    ...typographyScale.h3,
    ...fonts.medium,
    marginLeft: spacing.lg,
  },
  soonPill: {
    marginLeft: 'auto',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: radius.sm,
  },
  soonText: {
    ...typographyScale.caption,
    ...fonts.bold,
  },
});
